import { useCallback, useEffect, useState } from "react";

const STORAGE_PREFIX = "ui-pref:";

/**
 * Reads a stored preference, returning the fallback if it is missing, invalid or unreadable.
 */
const readStored = (key, fallback, allowedValues) => {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key);
    if (raw === null) return fallback;

    const stored = JSON.parse(raw);
    return !allowedValues || allowedValues.includes(stored) ? stored : fallback;
  } catch {
    // Storage blocked (private browsing) or stale/corrupt JSON — fall back silently.
    return fallback;
  }
};

/**
 * Custom hook that behaves like useState, but remembers the value in localStorage
 * so it survives page reloads and new sessions. Intended for view preferences
 * (selected tab, page size, sort order) — not for form data or anything sensitive,
 * since the value is per-browser and is not cleared on logout.
 *
 * @param {string} key - Stable key for this preference, e.g. "logs.displayTab".
 *                       Namespaced under "ui-pref:" in localStorage.
 * @param {*} defaultValue - Used on first visit, or when the stored value is no longer allowed.
 * @param {Array} [allowedValues] - For preferences with a fixed set of options. A stored value
 *                                  outside the set falls back to the default, so renaming an
 *                                  option cannot strand a returning user on a dead selection.
 *
 * @returns {Array} [value, setValue, reset] - setValue matches the useState setter
 *                                             (accepts a value or an updater function).
 *
 * @example
 * const [tab, setTab] = usePersistedState("logs.displayTab", "Task Logs", Object.values(LOG_TABS));
 */
const usePersistedState = (key, defaultValue, allowedValues) => {
  const [value, setValue] = useState(() => readStored(key, defaultValue, allowedValues));

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
    } catch {
      // Quota exceeded or storage blocked — the preference just won't persist.
    }
  }, [key, value]);

  const reset = useCallback(() => setValue(defaultValue), [defaultValue]);

  return [value, setValue, reset];
};

export default usePersistedState;
